import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { LogIn, UserPlus, CloudUpload } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Panda from "../components/Panda";
import { CurveBottom, Cloud } from "../components/Decor";
import { useAuth } from "../context/AuthContext";
import { useProgress } from "../context/ProgressContext";
import { getErrorMessage } from "../lib/api";

const inputCls = "mt-1.5 h-12 rounded-full px-5 border-2 border-[#c9ecf5] focus-visible:ring-[#1b8bc9]";

const AuthPage = () => {
  const { login, register } = useAuth();
  const { panda, bamboo } = useProgress();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(location.state?.tab || "login");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: location.state?.email || "", password: "" });
  const redirectTo = location.state?.redirect || "/lessons";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === "login") {
        const u = await login(form.email, form.password);
        toast.success(`Welcome back, ${u.name}!`);
      } else {
        const u = await register(form.email, form.password, form.name);
        toast.success(`Account created - welcome, ${u.name}!`, { description: "Your progress is now saved to your account." });
      }
      navigate(redirectTo);
    } catch (err) {
      toast.error(getErrorMessage(err, tab === "login" ? "Could not log in" : "Could not create account"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="auth-page">
      <section className="relative bg-[#1b8bc9] overflow-hidden">
        <Cloud className="absolute left-[6%] top-8 hidden md:block cloud-drift" size={180} />
        <Cloud className="absolute right-[8%] top-16 hidden md:block cloud-drift-slow" face size={200} />
        <div className="relative max-w-5xl mx-auto px-6 pt-14 pb-28 text-center">
          <h1 className="text-white font-bold text-4xl sm:text-5xl" style={{ fontFamily: "Literata, serif" }}>Save your progress</h1>
          <p className="text-white/90 text-lg mt-4 max-w-xl mx-auto">Create a free family account so your panda, bamboo and worksheets follow you to any device.</p>
        </div>
        <CurveBottom color="#ffffff" />
      </section>

      <section className="max-w-5xl mx-auto px-6 py-10 grid md:grid-cols-[1fr_1.1fr] gap-12 items-start">
        <div className="flex flex-col items-center text-center">
          <div className="bg-[#e9f9fd] rounded-[40px] p-8 w-full max-w-xs flex justify-center">
            <Panda size={200} accessory={panda?.accessory || "bowtie"} scarf={panda?.scarf || "#1b8bc9"} item="coin" animate />
          </div>
          {panda?.name ? (
            <div className="mt-5 bg-[#fff8e1] rounded-2xl p-4 text-sm text-[#8a6d00] flex items-start gap-3 text-left">
              <CloudUpload className="flex-shrink-0 mt-0.5" size={20} />
              <p><span className="font-bold">{panda.name}</span> and your {bamboo} bamboo from this browser will be moved into your account when you sign in.</p>
            </div>
          ) : (
            <p className="mt-5 text-gray-600 text-sm">No panda yet? <Link to="/panda" className="text-[#1b8bc9] font-bold hover:underline">Create one first</Link> - or sign in to load yours.</p>
          )}
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 rounded-full bg-[#e9f9fd] p-1 h-auto">
              <TabsTrigger value="login" className="rounded-full py-2 font-bold data-[state=active]:bg-[#1b8bc9] data-[state=active]:text-white" data-testid="tab-login">Log in</TabsTrigger>
              <TabsTrigger value="register" className="rounded-full py-2 font-bold data-[state=active]:bg-[#e5356f] data-[state=active]:text-white" data-testid="tab-register">Sign up</TabsTrigger>
            </TabsList>

            <form onSubmit={submit} className="mt-6 space-y-5" data-testid="auth-form">
              <TabsContent value="register" className="m-0">
                <Label htmlFor="name" className="font-bold text-[#0c4a6e]">Your name</Label>
                <Input id="name" value={form.name} onChange={set("name")} placeholder="e.g. Dana" required={tab === "register"} maxLength={60} className={inputCls} data-testid="auth-name-input" />
              </TabsContent>
              <div>
                <Label htmlFor="email" className="font-bold text-[#0c4a6e]">Email</Label>
                <Input id="email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" required className={inputCls} data-testid="auth-email-input" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="font-bold text-[#0c4a6e]">Password</Label>
                  {tab === "login" && <Link to="/forgot-password" className="text-xs font-bold text-[#1b8bc9] hover:underline" data-testid="forgot-password-link">Forgot password?</Link>}
                </div>
                <Input id="password" type="password" value={form.password} onChange={set("password")} placeholder={tab === "register" ? "At least 6 characters" : "Your password"} required minLength={6} className={inputCls} data-testid="auth-password-input" />
              </div>
              <button
                type="submit"
                disabled={busy}
                className={`w-full btn-pill-lg text-white shadow-lg inline-flex items-center justify-center gap-2 disabled:opacity-60 ${tab === "login" ? "bg-[#1b8bc9] hover:bg-[#156f9f]" : "bg-[#e5356f] hover:bg-[#c92a5e]"}`}
                data-testid="auth-submit-button"
              >
                {tab === "login" ? <LogIn size={20} /> : <UserPlus size={20} />}
                {busy ? "Please wait..." : tab === "login" ? "Log in" : "Create account"}
              </button>
            </form>
          </Tabs>
          <p className="mt-5 text-center text-sm text-gray-500">
            {tab === "login" ? (
              <>New here? <button onClick={() => setTab("register")} className="text-[#e5356f] font-bold hover:underline">Create a free account</button></>
            ) : (
              <>Already have an account? <button onClick={() => setTab("login")} className="text-[#1b8bc9] font-bold hover:underline">Log in</button></>
            )}
          </p>
        </div>
      </section>
    </div>
  );
};

export default AuthPage;
